import { normalizeImportedAssetFileId } from '@puppycad/cad-core'
import type { FileInfo } from '@puppycad/proto'

/**
 * 后端导出 GLB 时用于避免文件名冲突的 uuid 后缀格式。
 *
 * 说明：只匹配 `.glb_<uuid>` / `.glb|<uuid>` 结尾，避免误剥离普通文件名中的分隔符内容。
 */
const GLB_EXPORT_UUID_SUFFIX_RE = /(\.glb)(?:[|_][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/iu

/**
 * 外部引用文件的最小描述。
 *
 * 设计说明：
 * 1. 引用解析器来自 cad-core，返回的 `file` 只保证具备导入链路真正依赖的最小字段；
 * 2. web-client 自身继续使用 proto 生成的 `FileInfo` 作为正式运行时类型；
 * 3. 这里通过最小公共子集做一次收口，避免因为来源类型不同引入不必要的类型耦合。
 */
export type ResolvedReferenceFileLike = {
  /** 后端文件 ID；外部引用缺少 external_file_id 时可以为空。 */
  fileId: string
  /** 文件类型，当前链路通常是 `.glb`。 */
  kind: string
  /** 文件 UUID，优先使用 manifest 中的 external_file_uuid。 */
  uuid: string
  /** 文件大小；引用解析器可能不返回该字段。 */
  size?: bigint
}

/**
 * GetFile 的下载寻址参数。
 *
 * 设计说明：
 * 1. 该类型只表达协议入参，不代表项目文件或解析资产；
 * 2. fileId 可为空，后端可按 fileUuid 定位外部引用；
 * 3. 这层隔离避免把 external_file_uuid 误写入 FileInfo.fileId。
 */
export type ReferencedSourceDownloadTarget = {
  /** GetFile.file_id，可为空；为空时后端按 fileUuid 定位。 */
  fileId: string
  /** GetFile.file_uuid，只允许后端真实外部引用 UUID，不承载前端运行时身份。 */
  fileUuid: string
}

/**
 * 外部引用源描述。
 *
 * 设计说明：
 * 1. assetFile 服务解析、缓存、装配组合等资产语义；
 * 2. downloadTarget 服务 GetFile 协议寻址；
 * 3. 两者必须保持单向依赖，下载参数不能反向污染资产身份。
 */
export type ReferencedSourceDescriptor = {
  /** 解析、缓存和装配组合使用的资产身份描述。 */
  assetFile: FileInfo
  /** GetFile 下载寻址参数，不能反向污染 assetFile。 */
  downloadTarget: ReferencedSourceDownloadTarget
}

/**
 * 归一化外部引用中的项目文件 ID。
 *
 * @param fileId 候选文件 ID。
 * @returns 可用于 Map 查找的项目内路径 key。
 */
export function normalizeReferenceFileId(fileId: string | undefined): string {
  return fileId ? normalizeImportedAssetFileId(fileId) : ''
}

/**
 * 生成外部引用文件 ID 的可匹配别名。
 *
 * 设计说明：
 * 1. 后端为了避免重名，可能把 GLB 导出成 `xxx.glb|uuid` 或 `xxx.glb_uuid`；
 * 2. 项目文件列表中也可能只保留原始 `xxx.glb`；
 * 3. 因此查找时同时尝试原始 key 与剥离导出后缀后的 key。
 *
 * @param fileId 候选文件 ID。
 * @returns 去重后的 normalized fileId 候选 key。
 */
export function resolveReferenceFileIdAliases(fileId: string | undefined): string[] {
  const normalizedFileId = normalizeReferenceFileId(fileId)
  if (!normalizedFileId) {
    return []
  }

  // 导出后缀只参与查找别名，不能改变原始文件身份。
  const strippedExportUuidSuffix = normalizedFileId.replace(GLB_EXPORT_UUID_SUFFIX_RE, '$1')
  return Array.from(new Set([
    normalizedFileId,
    strippedExportUuidSuffix,
  ].filter((key) => key.length > 0)))
}

/**
 * 将引用解析器返回的最小文件描述收口为 FileInfo。
 *
 * @param file 引用解析器返回的文件描述。
 * @param fileUuid manifest 中的 external_file_uuid。
 * @param fileIdHint manifest 中的 external_file_id 或项目内拼接后的 file_id。
 * @returns 供后续解析链路使用的 FileInfo。
 */
export function normalizeResolvedReferenceFile(
  file: ResolvedReferenceFileLike,
  fileUuid: string,
  fileIdHint?: string,
): FileInfo {
  const normalizedUuid = file.uuid?.trim() || fileUuid
  const normalizedFileId = file.fileId?.trim() || fileIdHint || ''
  return {
    ...file,
    uuid: normalizedUuid,
    fileId: normalizedFileId,
  } as FileInfo
}

/**
 * 基于项目文件描述创建引用源描述。
 *
 * @param file 项目文件列表中的真实 GLB 文件描述。
 * @returns 引用源描述。
 */
export function createProjectReferencedSourceDescriptor(file: FileInfo): ReferencedSourceDescriptor {
  return {
    assetFile: file,
    downloadTarget: {
      fileId: file.fileId,
      fileUuid: file.uuid,
    },
  }
}

/**
 * 基于外部引用身份创建引用源描述。
 *
 * 设计说明：
 * 1. assetFile 只描述解析、缓存和装配组合使用的资产身份，不承担 GetFile 寻址职责；
 * 2. downloadTarget 只描述 GetFile 入参，external_file_id 缺失时 fileId 保持空字符串；
 * 3. external_file_uuid 是外部引用身份和后端下载 UUID，不能被写入 fileId。
 *
 * @param fileUuid manifest 中的 external_file_uuid。
 * @param fileIdHint 已按项目 ID 拼接后的 GetFile.file_id，可为空。
 * @param projectFile 项目文件列表中命中的 GLB 文件，存在时只提供路径与展示字段参考。
 * @returns 引用源描述。
 */
export function createExternalReferencedSourceDescriptor(
  fileUuid: string,
  fileIdHint: string | undefined,
  projectFile: FileInfo | undefined,
): ReferencedSourceDescriptor | undefined {
  const normalizedUuid = fileUuid.trim()
  const normalizedFileId = normalizeReferenceFileId(fileIdHint ?? projectFile?.fileId)
    .replace(GLB_EXPORT_UUID_SUFFIX_RE, '$1')
  if (!normalizedUuid) {
    return undefined
  }

  // 项目文件只作为资产字段补充，下载寻址仍以 manifest 外部引用身份为准。
  const assetFile = {
    ...(projectFile ?? {}),
    fileId: normalizedFileId,
    uuid: normalizedUuid,
    kind: projectFile?.kind ?? '.glb',
  } as FileInfo

  return {
    assetFile,
    downloadTarget: {
      fileId: normalizedFileId,
      fileUuid: normalizedUuid,
    },
  }
}
